import React, { useEffect, useMemo, useState } from 'react';
import styles from './Header.module.css';
import { FaBars, FaTimes } from 'react-icons/fa';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dropdown, Space } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { getTranslation, normalizeLang } from '../../../helpers/translation';

const LANGS = [
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'it', label: 'IT' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
  { code: 'pt', label: 'PT' },
  { code: 'nl', label: 'NL' },
  { code: 'da', label: 'DA' },
  { code: 'pl', label: 'PL' },
  { code: 'ua', label: 'UA' },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { lang: rawLang } = useParams();
  const lang = normalizeLang(rawLang);
  const navigate = useNavigate();
  const location = useLocation();

  const t = useMemo(() => getTranslation(lang), [lang]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setIsOpen(false);
  }, [location.pathname, location.hash]); // закриваємо меню після навігації

  const openMenu = () => setIsOpen(true);
  const closeMenu = () => setIsOpen(false);

  const hasLangPrefix = !!rawLang;
  const basePath = hasLangPrefix ? `/${lang}` : '/';
  const linkBase = hasLangPrefix ? `/${lang}/` : '/';
  const norm = p => (p.endsWith('/') ? p.slice(0, -1) : p);

  const handleAnchorClick = e => {
    setIsOpen(false);

    const href = e.currentTarget.getAttribute('href');
    if (!href) return;
    const idx = href.indexOf('#');
    if (idx === -1) return;

    const id = href.slice(idx + 1);
    const targetBase = href.slice(0, idx) || '/';

    const sameBase = norm(targetBase) === norm(linkBase);
    const onHomeNow = norm(location.pathname || '/') === norm(basePath);

    if (sameBase && onHomeNow) {
      e.preventDefault();
      const newUrl = id === 'home' ? linkBase : `${linkBase}#${id}`;
      window.history.replaceState(null, '', newUrl);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Зміна мови: тепер БЕЗ перенесення хеша (щоб не стрибало на #projects тощо)
  const changeLang = newCode => {
    // ❗ якщо хочеш зберігати хеш — заміни '' на (location.hash || '')
    const hash = ''; // не переносимо якір
    const segs = location.pathname.split('/').filter(Boolean); // ['blog'] або ['de','blog'] ...
    const isPrefixed = hasLangPrefix;

    let nextPath = '/';

    if (!isPrefixed) {
      // EN без префікса
      if (newCode === 'en') {
        nextPath = `/${segs.join('/')}`;
        if (nextPath === '/') nextPath = '/'; // корінь
      } else {
        const rest = segs.join('/');
        nextPath = rest.length ? `/${newCode}/${rest}` : `/${newCode}/`;
      }
    } else {
      // зараз з префіксом
      if (newCode === 'en') {
        const rest = segs.slice(1).join('/');
        nextPath = rest.length ? `/${rest}` : `/`;
      } else {
        segs[0] = newCode;
        nextPath = `/${segs.join('/')}`;
      }
    }

    navigate(`${nextPath}${hash}`);
    setIsOpen(false);
  };

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];
  const languageMenuItems = LANGS.map(({ code, label }) => ({
    key: code,
    label: (
      <button className={styles.langItemBtn} onClick={() => changeLang(code)}>
        <span className={styles.code}>{label}</span>
      </button>
    ),
  }));

  const linkTo = anchor => `${linkBase}#${anchor}`;

  return (
    <header className={styles.header}>
      <nav className={styles.navbar}>
        <div className={styles.burger} onClick={isOpen ? closeMenu : openMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </div>

        <ul className={`${styles.navLinks} ${isOpen ? styles.active : ''}`}>
          <li>
            <NavLink to={linkTo('home')} onClick={handleAnchorClick}>
              {t.header.homeLink}
            </NavLink>
          </li>
          <li>
            <NavLink to={linkTo('about')} onClick={handleAnchorClick}>
              {t.header.aboutUsLink}
            </NavLink>
          </li>
          <li>
            <NavLink to={linkTo('services')} onClick={handleAnchorClick}>
              {t.header.servicesLink}
            </NavLink>
          </li>
          <li>
            <NavLink to={linkTo('projects')} onClick={handleAnchorClick}>
              {t.header.ourProjectsLink}
            </NavLink>
          </li>
          <li>
            <NavLink to={linkTo('contact')} onClick={handleAnchorClick}>
              {t.header.contact}
            </NavLink>
          </li>

          <li>
            <NavLink
              to={lang === 'en' ? `/blog` : `/${lang}/blog`}
              onClick={handleAnchorClick}
            >
              {t.header.blogLink || 'Blog'}
            </NavLink>
          </li>

          <li className={styles.language}>
            <Dropdown menu={{ items: languageMenuItems }} trigger={['click']}>
              <button className={styles.langTrigger}>
                <span className={styles.code}>{currentLang.label}</span>
                <DownOutlined className={styles.langCaret} />
              </button>
            </Dropdown>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;
